import { BaseProduct } from "@CommonTypes/merchflow/pog/product";
import { ReactNode } from "react";
import {
  Color,
  Flex,
  Grid,
  HorizontalDivider,
  Scroller,
  SmallNumberInput,
  Text,
} from "src/elements";
import { ProductImage } from "../../../Item/Image";
import { CdtEditorProduct, ItemCdtEditor } from "./ItemCdtEditor";
import {
  BULK_POUNDS,
  countedWeightPounds,
  resolveCountedWeightPounds,
  type CountedUnitStyle,
  type ResolveWeightOptions,
} from "@CommonUtils/pog/productWeight";
import { formatUnitWeight } from "src/components/Planogram/common/shelfWeightLabel";
import { useTranslation } from "src/i18n";

const DetailRow = ({ label, value }: { label: string; value: string | number }) => (
  <>
    <Text variant="caption1" color={Color.primaryActive}>
      {label}:
    </Text>

    <Text variant="caption1" color={Color.textMain}>
      {value}
    </Text>
  </>
);

interface Props {
  item: BaseProduct;
  selectedItems: CdtEditorProduct[];
  cdtSourceItems: CdtEditorProduct[];
  saveChanges: () => void;
  beforeCdt?: ReactNode;
  children?: ReactNode;
  statedWeightsTrusted?: boolean;
}

export const ProductDetailsCommon = ({
  item,
  selectedItems,
  cdtSourceItems,
  saveChanges,
  beforeCdt,
  children,
  statedWeightsTrusted = true,
}: Props) => {
  const { t } = useTranslation("common");

  const weightOptions: ResolveWeightOptions = statedWeightsTrusted
    ? {}
    : { ignoredStyles: new Set<CountedUnitStyle>(["UNIT", "TRAY", "CASE"]) };
  const unitWeight = resolveCountedWeightPounds(item, "UNIT", weightOptions);
  const weightLabel = formatUnitWeight(
    unitWeight?.pounds ?? null,
    unitWeight?.provenance ?? null,
    (countedWeightPounds(item, "UNIT", weightOptions) ?? 0) >= BULK_POUNDS,
    t,
  );

  return (
    <Scroller>
      <Flex padding="10px" column gap="10px">
        <Flex>
          <ProductImage productCode={item.productCode} fixedSize="100px" size="small" />

          <Flex direction="column" flexGrow={1} padding="0 10px" gap="10px">
            <Text variant="small1" color={Color.primaryActive}>
              {item.name}
            </Text>

            <Grid columns="1fr auto" justify="space-between" gap="10px">
              <DetailRow label="Item ID" value={item.productCode} />
              <DetailRow label="Category" value={item.categoryCode} />
              <DetailRow label="Brand" value={item.brand} />
              {weightLabel && (
                <DetailRow label={t("planogramProductDetails.weight")} value={weightLabel} />
              )}
            </Grid>
          </Flex>
        </Flex>

        {beforeCdt}

        {beforeCdt && <HorizontalDivider />}

        <ItemCdtEditor
          selectedItems={selectedItems}
          cdtSourceItems={cdtSourceItems}
          saveChanges={saveChanges}
        />

        {item.rangedInBiggerOrSmallerStores && (
          <Grid columns="auto 1fr" justify="space-between" gap="10px">
            <>
              <DetailRow
                label="Ranged Approved Smaller"
                value={item.rangedInBiggerOrSmallerStores.approvedSmaller}
              />
              <DetailRow
                label="Ranged All Smaller"
                value={item.rangedInBiggerOrSmallerStores.allSmaller}
              />
              <DetailRow
                label="Ranged Approved Bigger"
                value={item.rangedInBiggerOrSmallerStores.approvedBigger}
              />
              <DetailRow
                label="Ranged All Bigger"
                value={item.rangedInBiggerOrSmallerStores.allBigger}
              />
            </>
          </Grid>
        )}

        <Grid columns="1fr 1fr" gap="10px" margin="10px 0 0 0">
          <SmallNumberInput
            label="Min. Facings:"
            variant="sidebuttons"
            textVariant="caption1"
            textColor={Color.primaryActive}
            width="20px"
            value={item.minFacings}
            setValue={(value) => {
              item.minFacings = value;
              saveChanges();
            }}
            min={1}
          />

          <SmallNumberInput
            label="Max. Facings:"
            variant="sidebuttons"
            textVariant="caption1"
            textColor={Color.primaryActive}
            width="20px"
            value={item.maxFacings}
            setValue={(value) => {
              item.maxFacings = value;
              saveChanges();
            }}
          />
        </Grid>

        {children}
      </Flex>
    </Scroller>
  );
};
